// The rules of the client application journey (SEP-263, slice 6).
//
// THE BANK DRIVES EVERY RULE. `client-application-questions.ts` holds the
// questions, the kinds, the options and the `showWhen` conditions, and this
// module holds nothing but the rules that read them. No question, no label and
// no option is retyped here.
//
// # WHY THE RULES LIVE IN THE CONTRACT
//
// The browser is not a validator. A public write surface takes whatever a
// client sends, so the SERVER must check each answer against the same bank the
// browser rendered (SEP-263.13). The server must not import the web app, so the
// one copy of the rules sits beside the bank, in the contract, and both sides
// call it. A rule that the two sides held separately would drift.
//
// # THE THREE RULES
//
//   1. VISIBILITY. A question whose `showWhen` does not hold is HIDDEN. A
//      hidden question holds no answer and is never required, and
//      {@link pruneClientApplicationAnswers} removes the answer that a closed
//      branch left behind. The prune repeats until the map is stable, because
//      closing a parent closes its children.
//   2. THE SHAPE. Each kind admits one shape and one range. A wrong shape is a
//      REFUSAL: the save writes nothing and the page places the message under
//      the field.
//   3. COMPLETENESS. A visible required question with a blank answer is
//      INCOMPLETE, and incomplete is not the same as wrong. A save never
//      refuses a blank answer, because the client saves a half-filled section
//      and comes back to it (SEP-263.8, SEP-263.18). Completeness gates the
//      step forward and drives the progress indication, and slice 7 gates the
//      submission on it.
//   4. THE FORM RULES. A rule that reads more than one answer, or that
//      compares an answer with today, is a form rule of the bank
//      (`client-application-form-rules.ts`, SEP-293.3). The caller gives the
//      date of today, because the contract never reads the clock.
//
// # WHAT THIS MODULE NEVER DOES
//
// It never invents a question, it never writes, and it never judges a value the
// bank does not describe. A question the bank does not hold has no rule, so its
// answer is dropped rather than stored under an unknown id.

import {
    CLIENT_APPLICATION_THIRD_PARTY_OPENER_ID,
    CLIENT_APPLICATION_THIRD_PARTY_QUESTION_IDS,
    CLIENT_APPLICATION_FORM_VERSION,
    CLIENT_APPLICATION_FORM_VERSIONS,
    CLIENT_APPLICATION_FREQUENCY_OPTIONS,
    CLIENT_APPLICATION_FREQUENCY_QUESTION_IDS,
    CLIENT_APPLICATION_SECTIONS,
    clientApplicationFrequencyOtherId,
    clientApplicationQuestion,
    clientApplicationSection,
    clientApplicationContextBank,
    clientApplicationContextVersion,
    clientApplicationSections,
    CLIENT_APPLICATION_PREVIOUS_ADDRESS_GATE,
    CLIENT_APPLICATION_TAX_RESIDENCY_OPTIONAL_ROWS,
    isClientApplicationQuestionRequired,
    isClientApplicationQuestionVisible,
    isInClientApplicationFormVersionSpan,
    lockedClientApplicationOptions,
    type ClientApplicationFormContext,
    type ClientApplicationQuestion,
    type ClientApplicationSection,
} from './client-application-questions';
import type { ClientApplicationFormDefinition } from './client-application-forms';
import { COUNTRIES } from './countries';
import { countryCodeFromNationality } from './nationality-names';
import { clientApplicationIsaWithGeneralInvestment } from './client-application-isa-gia';
import { CLIENT_APPLICATION_RISK_QUESTION_CHANGES } from './client-application-scored-risk';

// ---------------------------------------------------------------------------
// The answer shapes
// ---------------------------------------------------------------------------

/**
 * One stored answer.
 *
 * A `multi_select` holds an array of option labels. An `acknowledgement` and a
 * `tick` hold a boolean, because a tick is true or it is absent. A `number` and a
 * `currency` hold a number, so the stored value is the money and never the
 * presentation of it. Every other kind holds a string.
 */
export type ClientApplicationAnswer = string | number | boolean | readonly string[] | null;

/** The answers of one application, keyed by question id. */
export type ClientApplicationAnswers = Readonly<Record<string, ClientApplicationAnswer>>;

/** One field-level refusal, placed under the field that caused it. */
export interface ClientApplicationFieldError {
    readonly sectionId: string;
    readonly questionId: string;
    readonly message: string;
}

// ---------------------------------------------------------------------------
// The limits
// ---------------------------------------------------------------------------

/** The longest single-line answer the journey stores. */
export const CLIENT_APPLICATION_SHORT_TEXT_MAX = 500;
/** The longest multi-line answer the journey stores. */
export const CLIENT_APPLICATION_LONG_TEXT_MAX = 5_000;
/** The largest money answer the journey stores. */
export const CLIENT_APPLICATION_AMOUNT_MAX = 1_000_000_000_000;
/** The largest plain-number answer the journey stores. */
export const CLIENT_APPLICATION_NUMBER_MAX = 1_000_000;
/** The earliest date the journey accepts. */
export const CLIENT_APPLICATION_EARLIEST_DATE = '1900-01-01';
/** The latest date the journey accepts. */
export const CLIENT_APPLICATION_LATEST_DATE = '2100-01-01';

const COUNTRY_CODES: ReadonlySet<string> = new Set(COUNTRIES.map((country) => country.code));

// ---------------------------------------------------------------------------
// Blank
// ---------------------------------------------------------------------------

/** Whether an answer holds nothing. A false tick is blank, because it is no tick. */
export function isBlankClientApplicationAnswer(value: ClientApplicationAnswer): boolean {
    if (value === null || value === undefined) return true;
    if (typeof value === 'string') return value.trim() === '';
    if (typeof value === 'boolean') return value === false;
    if (Array.isArray(value)) return value.length === 0;
    return false;
}

// ---------------------------------------------------------------------------
// Answers saved under an older bank
// ---------------------------------------------------------------------------

/**
 * One step that reads answers that an older bank saved in the shape that a
 * later bank holds.
 *
 * A step runs for each target version in its span `[introducedIn,
 * retiredIn)`. Thus a reader of a version 2.0 snapshot never runs a step
 * that only version 3.0 needs, and the answers that the IM reads stay the
 * answers that the client gave.
 */
interface ClientApplicationUpgradeStep {
    /** The ticket that added the step. */
    readonly id: string;
    readonly introducedIn?: string;
    readonly retiredIn?: string;
    /**
     * True when the step reads only the first section, which each form shares
     * (SEP-293). Such a step runs for each form. Each other step runs for the
     * individual form only, because it reads the ids of that form and the
     * versions of its version line.
     */
    readonly shared?: true;
    /**
     * True when the step runs only for a STORED DRAFT
     * ({@link ClientApplicationUpgradeOptions.storedDraft}). Such a step
     * repairs a draft that a rule of the current version refuses, so the
     * validator never runs it: a new answer that breaks the rule must still
     * refuse.
     */
    readonly storedDraftOnly?: true;
    /**
     * True when the step never runs for a STORED DRAFT. Such a step reads a
     * submission of an older version in the shape of a later version, so that
     * the submission still supplies a fact find. A draft does not get it,
     * because the client must read the new question and answer it again
     * (SEP-293.1).
     */
    readonly snapshotOnly?: true;
    readonly apply: (out: Record<string, ClientApplicationAnswer>) => void;
}

/** What a reader of a stored map tells the upgrade. */
export interface ClientApplicationUpgradeOptions {
    /**
     * True when the map is a stored draft that the server reads, before the
     * client sees it or before a save merges onto it. The prune sets it.
     */
    readonly storedDraft?: boolean;
    /**
     * The form of the answers (SEP-293). Absent means the individual form.
     * Each other form runs only the steps that each form shares.
     */
    readonly form?: ClientApplicationFormDefinition;
}

/**
 * The upgrade steps, in the order they run.
 *
 * Each step of SEP-280 and SEP-286 changed the bank inside version 2.0, so
 * each one runs for version 2.0 and for each later version. The step of
 * SEP-285 runs for version 2.0 only, because version 3.0 retires its gate.
 * The steps of SEP-293.4, SEP-293.7 and SEP-293.1 run for version 3.0 and
 * later. The step of SEP-293.7 runs only for a stored draft. The last step of
 * SEP-293.1 never runs for a stored draft.
 */
const UPGRADE_STEPS: readonly ClientApplicationUpgradeStep[] = [
    {
        // A question opens the third party of Part 10 now, because each
        // visible question needs an answer. A map that names a third party
        // and holds no answer to the opener reads as Yes, so the answers stay
        // visible in a draft and in a snapshot.
        id: 'third party opener',
        shared: true,
        apply: (out) => {
            if (out[CLIENT_APPLICATION_THIRD_PARTY_OPENER_ID] !== undefined) return;
            const named = CLIENT_APPLICATION_THIRD_PARTY_QUESTION_IDS.some(
                (id) => !isBlankClientApplicationAnswer(out[id] ?? null),
            );
            if (named) out[CLIENT_APPLICATION_THIRD_PARTY_OPENER_ID] = 'Yes';
        },
    },
    {
        // The account type was ONE choice, and it is a list now. A string
        // becomes a list of one.
        id: 'SEP-280 account types',
        shared: true,
        apply: (out) => {
            const accountTypes = out['account_types'];
            if (typeof accountTypes === 'string') {
                out['account_types'] = accountTypes.trim() === '' ? null : [accountTypes];
            }
        },
    },
    {
        // SEP-293: the firm names a small self-administered scheme "SSAS".
        // The option was "SASS" before, and the stored value is the label,
        // so a stored draft reads the new label. A snapshot keeps the label
        // that the client chose.
        id: 'SEP-293 SSAS account type',
        shared: true,
        storedDraftOnly: true,
        apply: (out) => {
            const accountTypes = out['account_types'];
            if (Array.isArray(accountTypes) && accountTypes.includes('SASS')) {
                out['account_types'] = accountTypes.map((entry) =>
                    entry === 'SASS' ? 'SSAS' : entry,
                );
            }
        },
    },
    {
        // Each frequency of Part 5 was free text, and it is a closed list now.
        // A text that is not an option becomes `Other`, and the client's own
        // words move to the `Other` text box, unless that box already holds an
        // answer.
        id: 'SEP-280 frequencies',
        apply: (out) => {
            const frequencyOptions: readonly string[] = CLIENT_APPLICATION_FREQUENCY_OPTIONS;
            for (const id of CLIENT_APPLICATION_FREQUENCY_QUESTION_IDS) {
                const value = out[id];
                if (typeof value !== 'string' || value.trim() === '') continue;
                if (frequencyOptions.includes(value)) continue;
                out[id] = 'Other';
                const otherId = clientApplicationFrequencyOtherId(id);
                if (isBlankClientApplicationAnswer(out[otherId] ?? null)) out[otherId] = value;
            }
        },
    },
    {
        // Rows 2 and 3 of tax residency are optional now, and each opens with
        // a yes or no question. A draft that already fills a row, and that has
        // no answer to that question, gets the answer Yes, so the row stays
        // open. A filled row 3 also opens row 2, because row 3 opens only
        // while row 2 is open.
        id: 'SEP-286 tax residency rows',
        apply: (out) => {
            const rows = CLIENT_APPLICATION_TAX_RESIDENCY_OPTIONAL_ROWS;
            let lastFilled = -1;
            rows.forEach((row, index) => {
                if (
                    row.questionIds.some((id) => !isBlankClientApplicationAnswer(out[id] ?? null))
                ) {
                    lastFilled = index;
                }
            });
            for (let index = 0; index <= lastFilled; index++) {
                const openerId = rows[index]!.openerId;
                if (isBlankClientApplicationAnswer(out[openerId] ?? null)) out[openerId] = 'Yes';
            }
        },
    },
    {
        // The previous address opens when the client has not lived at the
        // current address for 3 years. A draft that already holds a previous
        // address, and that has no answer to that question, gets the answer
        // No, so the previous address stays open. Form version 3.0 retires
        // the gate (SEP-293.5), so the step runs for version 2.0 only.
        id: 'SEP-285 previous address gate',
        retiredIn: '3.0',
        apply: (out) => {
            const gate = CLIENT_APPLICATION_PREVIOUS_ADDRESS_GATE;
            if (
                isBlankClientApplicationAnswer(out[gate.gateId] ?? null) &&
                gate.questionIds.some((id) => !isBlankClientApplicationAnswer(out[id] ?? null))
            ) {
                out[gate.gateId] = 'No';
            }
        },
    },
    {
        // Form version 3.0 takes the nationality from the country list
        // (SEP-293.4). A draft that holds the free text of version 2.0, and
        // no country, gets the country that the text names. Two names give
        // the second nationality too. A text that the step cannot read in
        // full gives nothing, and the client chooses again. The prune keeps
        // the text until the client chooses a country.
        id: 'SEP-293.4 nationality from the country list',
        introducedIn: '3.0',
        apply: (out) => {
            const text = out['nationality'];
            if (typeof text !== 'string' || text.trim() === '') return;
            if (!isBlankClientApplicationAnswer(out['country_of_nationality'] ?? null)) return;
            const [first, second] = clientApplicationNationalityCodes(text);
            if (first !== undefined) out['country_of_nationality'] = first;
            if (
                second !== undefined &&
                isBlankClientApplicationAnswer(out['second_country_of_nationality'] ?? null)
            ) {
                out['second_country_of_nationality'] = second;
            }
        },
    },
    {
        // Form version 3.0 opens an ISA only beside a General Investment
        // Account (SEP-293.7). A stored draft that holds the ISA alone gets
        // the General Investment Account, so the client sees both ticked.
        // The validator never runs this step, so a save or a submission that
        // sends the ISA alone still refuses.
        id: 'SEP-293.7 an ISA brings a General Investment Account',
        introducedIn: '3.0',
        storedDraftOnly: true,
        apply: (out) => {
            const next = clientApplicationIsaWithGeneralInvestment(out['account_types']);
            if (Array.isArray(next)) out['account_types'] = [...(next as string[])];
        },
    },
    {
        // Form version 3.0 gives some options the words of the v6 fact find,
        // and each option keeps its meaning and its position (SEP-293.1). An
        // answer in the words of version 2.0 becomes the option at the same
        // position.
        id: 'SEP-293.1 option words of the v6 fact find',
        introducedIn: '3.0',
        apply: (out) => {
            for (const question of CLIENT_APPLICATION_SECTIONS.flatMap((s) => s.questions)) {
                const older = question.wordingIn?.['2.0']?.options;
                const current = question.options;
                if (older === undefined || current === undefined) continue;
                if (older.length !== current.length) continue;
                const value = out[question.id];
                if (typeof value !== 'string') continue;
                const index = older.indexOf(value);
                if (index >= 0 && !current.includes(value)) out[question.id] = current[index]!;
            }
        },
    },
    {
        // Form version 3.0 retires `investment_objective`, because the Fact
        // Find v3 adds the option Capital Preservation (SEP-293.1). Each
        // answer of version 2.0 is an option of `current_investment_objective`,
        // with one change of words.
        id: 'SEP-293.1 investment objective',
        introducedIn: '3.0',
        apply: (out) => {
            const value = out['investment_objective'];
            if (typeof value !== 'string' || value.trim() === '') return;
            if (!isBlankClientApplicationAnswer(out['current_investment_objective'] ?? null))
                return;
            const words =
                value === 'Balance of income and growth' ? 'Balance of Income and Growth' : value;
            const options: readonly string[] =
                clientApplicationQuestion('current_investment_objective')?.options ?? [];
            if (options.includes(words)) out['current_investment_objective'] = words;
        },
    },
    {
        // Form version 3.0 asks a yes or no question about communication and
        // support needs, and a detail box (SEP-293.1). A text of version 2.0
        // gives the answer Yes and fills the detail box.
        id: 'SEP-293.1 communication and support needs',
        introducedIn: '3.0',
        apply: (out) => {
            const text = out['support_needs'];
            if (typeof text !== 'string' || text.trim() === '') return;
            if (!isBlankClientApplicationAnswer(out['communication_needs'] ?? null)) return;
            out['communication_needs'] = 'Yes';
            if (isBlankClientApplicationAnswer(out['communication_needs_detail'] ?? null)) {
                out['communication_needs_detail'] = text;
            }
        },
    },
    {
        // Form version 3.0 asks four risk questions under a new id
        // (`client-application-scored-risk.ts`). A submission of version 2.0
        // gives its answer to the new id, as the option at the same position,
        // so that it still supplies the risk instrument of a fact find. This
        // is the positional map of SEP-288. A draft does not get the step.
        id: 'SEP-293.1 risk answers of a submission',
        introducedIn: '3.0',
        snapshotOnly: true,
        apply: (out) => {
            for (const change of CLIENT_APPLICATION_RISK_QUESTION_CHANGES) {
                const value = out[change.retiredQuestionId];
                if (typeof value !== 'string') continue;
                if (!isBlankClientApplicationAnswer(out[change.questionId] ?? null)) continue;
                const older = clientApplicationQuestion(change.retiredQuestionId, '2.0')?.options;
                const current = clientApplicationQuestion(change.questionId)?.options;
                const index = older?.indexOf(value) ?? -1;
                const words = index >= 0 ? current?.[index] : undefined;
                if (words !== undefined) out[change.questionId] = words;
            }
        },
    },
];

/**
 * The country codes that a free-text nationality names, without repeats.
 *
 * The whole text matches first, so a name such as "Trinidad & Tobago" gives
 * one country. If it does not match, the text splits at "/", "," and ";",
 * and each part matches as a whole. A part that does not match splits again
 * at "&" and the word "and". Each piece matches a code, an en-GB country
 * name, a known other name or a demonym ({@link countryCodeFromNationality}).
 *
 * If one piece matches nothing, or the text names more than two countries,
 * the result is empty. The form holds two nationalities only, and a partial
 * answer is worse than a blank one: the client chooses again.
 */
export function clientApplicationNationalityCodes(text: string): string[] {
    const whole = countryCodeFromNationality(text);
    if (whole !== null) return [whole];
    const codes: string[] = [];
    for (const part of text.split(/[/,;]/)) {
        if (part.trim() === '') continue;
        const partCode = countryCodeFromNationality(part);
        const pieces =
            partCode !== null
                ? [partCode]
                : part
                      .split(/&|\band\b/i)
                      .filter((piece) => piece.trim() !== '')
                      .map((piece) => countryCodeFromNationality(piece));
        for (const code of pieces) {
            if (code === null) return [];
            if (!codes.includes(code)) codes.push(code);
        }
    }
    return codes.length > 2 ? [] : codes;
}

/**
 * Read answers that an older bank saved in the shape that one version of the
 * bank holds. The default is the current version.
 *
 * A draft and a snapshot outlive a change to the bank, so every reader of a
 * stored map calls this first. A draft reads in the current version. A
 * snapshot reads in the version that it records, so a step for a later
 * version never changes what the IM reads. Each step is in
 * {@link UPGRADE_STEPS}, with its reason.
 *
 * It never drops an answer. It invents only the Yes of an opener, the No
 * of the address gate, the country code of a free-text nationality, the Yes
 * of communication needs and the words of an option at the same position,
 * and only when an answer of the client gives it. For a map that is not a
 * stored draft, it also gives a retired risk answer to its new id
 * (SEP-293.1). For a stored draft
 * (`options.storedDraft`), it also adds the General Investment Account
 * beside an ISA that stands alone (SEP-293.7). A map that is already in the
 * new shape comes back equal.
 */
export function upgradeClientApplicationAnswers(
    answers: ClientApplicationAnswers,
    version: string = CLIENT_APPLICATION_FORM_VERSION,
    options: ClientApplicationUpgradeOptions = {},
): ClientApplicationAnswers {
    const out: Record<string, ClientApplicationAnswer> = { ...answers };
    // A test bank with its own version line is not the individual form.
    const individual =
        options.form === undefined
            ? (CLIENT_APPLICATION_FORM_VERSIONS as readonly string[]).includes(version)
            : options.form.id === 'individual';
    for (const step of UPGRADE_STEPS) {
        if (step.storedDraftOnly === true && options.storedDraft !== true) continue;
        if (step.snapshotOnly === true && options.storedDraft === true) continue;
        if (!individual) {
            // The span of a step is a span of the individual version line,
            // so a step for each form has no span.
            if (step.shared === true) step.apply(out);
            continue;
        }
        if (isInClientApplicationFormVersionSpan(step, version)) step.apply(out);
    }
    return out;
}

// ---------------------------------------------------------------------------
// Visibility
// ---------------------------------------------------------------------------

/**
 * The visible questions of one section, given the answers so far.
 *
 * The section must come from the same version as `context.version`.
 */
export function visibleClientApplicationQuestions(
    section: ClientApplicationSection,
    answers: ClientApplicationAnswers,
    context: ClientApplicationFormContext = {},
): readonly ClientApplicationQuestion[] {
    return section.questions.filter((question) =>
        isClientApplicationQuestionVisible(question, answers, context),
    );
}

/**
 * Drop every answer that no visible question of the bank holds.
 *
 * Three things go: an answer under an id the bank does not know, the answer
 * of a question that the version retired, and the answer of a question the
 * client closed. The default version is the current version, because only a
 * draft is pruned. The prune reads the map as a stored draft, so it also
 * runs the upgrade steps of a stored draft (SEP-293.7). The prune repeats
 * until the map stops
 * changing, because a closed parent closes the children that condition on it,
 * and one pass would leave a grandchild behind. A blank answer goes too, so a
 * cleared field is absent rather than stored empty. One retired answer
 * stays: the free-text nationality, while no country is chosen (SEP-293.4).
 */
export function pruneClientApplicationAnswers(
    answers: ClientApplicationAnswers,
    context: ClientApplicationFormContext = {},
): ClientApplicationAnswers {
    const version = clientApplicationContextVersion(context);
    const sections = clientApplicationSections(version, clientApplicationContextBank(context));
    let current: Record<string, ClientApplicationAnswer> = {};
    const upgraded = upgradeClientApplicationAnswers(answers, version, {
        storedDraft: true,
        form: context.form,
    });
    for (const [id, value] of Object.entries(upgraded)) {
        if (isBlankClientApplicationAnswer(value)) continue;
        current[id] = value;
    }
    // The bank is at most a few conditions deep, so a handful of passes always
    // reaches the fixed point. The loop stops on the first stable pass.
    for (let pass = 0; pass < 8; pass++) {
        const next: Record<string, ClientApplicationAnswer> = {};
        for (const section of sections) {
            for (const question of section.questions) {
                const value = current[question.id];
                if (value === undefined) continue;
                if (!isClientApplicationQuestionVisible(question, current, context)) continue;
                next[question.id] = value;
            }
        }
        // Each pass only ever drops an entry, so an unchanged size is a fixed
        // point.
        const stable = Object.keys(next).length === Object.keys(current).length;
        current = next;
        if (stable) break;
    }
    // A ticked "Not applicable" wins over an answer of its question, so a
    // stored map never holds both.
    for (const section of sections) {
        for (const question of section.questions) {
            if (question.notApplicableFor !== undefined && current[question.id] === true) {
                delete current[question.notApplicableFor];
            }
        }
    }
    // Version 3.0 retires the free-text nationality (SEP-293.4). When its
    // text names no country that the upgrade can read, the text stays until
    // the client chooses a country, so the words of the client are not lost.
    const nationalityText = upgraded['nationality'];
    if (
        (context.form === undefined || context.form.id === 'individual') &&
        typeof nationalityText === 'string' &&
        nationalityText.trim() !== '' &&
        current['country_of_nationality'] === undefined
    ) {
        current['nationality'] = nationalityText;
    }
    return current;
}

// ---------------------------------------------------------------------------
// The shape of one answer
// ---------------------------------------------------------------------------

function isCalendarDate(value: string): boolean {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;
    const parsed = new Date(`${value}T00:00:00.000Z`);
    if (Number.isNaN(parsed.getTime())) return false;
    // A date such as `2026-02-31` parses and rolls forward, so the round trip
    // is what proves the day exists.
    return parsed.toISOString().slice(0, 10) === value;
}

function numberRangeMessage(value: number, max: number): string | null {
    if (!Number.isFinite(value)) return 'Enter a number.';
    if (value < 0) return 'Enter a number that is zero or more.';
    if (value > max) return `Enter a number no larger than ${max.toLocaleString('en-GB')}.`;
    return null;
}

/**
 * The message for a mix of choices that the bank does not let go together.
 *
 * It answers null when the question sets no `combinableOptions`, when the
 * client chose one option or none, and when every choice is combinable.
 */
function combinationMessage(
    question: ClientApplicationQuestion,
    chosen: readonly string[],
): string | null {
    const combinable = question.combinableOptions;
    if (!combinable || chosen.length < 2) return null;
    if (chosen.every((entry) => combinable.includes(entry))) return null;
    return `Choose one option alone, or choose ${combinable.join(' and ')} together.`;
}

/**
 * The choices of a `multi_select` after the client presses one option.
 *
 * A press on a chosen option removes it. A press on an option that is not
 * chosen adds it, under the rule of `combinableOptions`: an option outside
 * that list replaces every choice, and an option inside the list replaces
 * each choice outside it. When the question sets no such list, the press adds
 * the option to the choices. The result keeps the order of the bank.
 *
 * The journey calls this for each press, so the control holds no rule of its
 * own, and the validator refuses each mix that this function cannot make.
 */
export function chooseClientApplicationOption(
    question: ClientApplicationQuestion,
    chosen: readonly string[],
    option: string,
): string[] {
    const inBankOrder = (set: ReadonlySet<string>) =>
        (question.options ?? []).filter((entry) => set.has(entry));
    if (chosen.includes(option)) {
        // An option that another chosen option brings with it stays chosen
        // (SEP-293.7): the General Investment Account stays while the ISA
        // stays.
        if (lockedClientApplicationOptions(question, chosen).includes(option)) {
            return inBankOrder(new Set(chosen));
        }
        return chosen.filter((entry) => entry !== option);
    }
    const combinable = question.combinableOptions;
    const kept =
        combinable === undefined
            ? chosen
            : combinable.includes(option)
              ? chosen.filter((entry) => combinable.includes(entry))
              : [];
    // A press also chooses each option that the pressed option brings with
    // it (SEP-293.7): a press on ISA also chooses the General Investment
    // Account.
    return inBankOrder(new Set([...kept, option, ...(question.impliedOptions?.[option] ?? [])]));
}

/**
 * The message for a proposed investment manager that the live roster does
 * not resolve to exactly one manager.
 *
 * The option VALUE is the name of the manager, so a name that two managers
 * carry names neither of them, and a name that no manager carries names
 * nobody. The server routes the application by the one manager a name
 * resolves to, and it never guesses.
 */
function investmentManagerMessage(
    name: string,
    investmentManagerNames: readonly string[],
): string | null {
    const matches = investmentManagerNames.filter((entry) => entry === name).length;
    if (matches === 0) return 'Choose an Investment Manager from the list.';
    if (matches > 1) {
        return 'Two Investment Managers have this name, so we cannot tell which one you mean. Please contact us.';
    }
    return null;
}

/** What a caller adds to the check of one answer. */
export interface ClientApplicationAnswerChecks {
    /**
     * The live roster, one entry for each manager. When the caller gives it,
     * the proposed investment manager must name exactly one manager of it.
     * When the caller does not give it, the answer is not checked against a
     * roster.
     */
    readonly investmentManagerNames?: readonly string[];
    /**
     * Do not check the mix of choices against `combinableOptions`. A save
     * sets it for an answer that the save does not change, so a mix that a
     * draft stored before the rule does not block the save of another
     * section. The submission never sets it.
     */
    readonly skipCombination?: boolean;
}

/**
 * Check one answer against the kind and the options of its question.
 *
 * It answers null when the answer is usable, and the message to place under
 * the field otherwise. A BLANK answer is always usable here: blank is a matter
 * of completeness and not of shape.
 */
export function validateClientApplicationAnswer(
    question: ClientApplicationQuestion,
    value: ClientApplicationAnswer,
    checks: ClientApplicationAnswerChecks = {},
): string | null {
    if (isBlankClientApplicationAnswer(value)) return null;

    switch (question.kind) {
        case 'acknowledgement':
            return typeof value === 'boolean' ? null : 'Tick the box to accept.';

        case 'tick':
            return typeof value === 'boolean' ? null : 'Tick the box, or leave it empty.';

        case 'multi_select': {
            if (!Array.isArray(value)) return 'Choose from the list.';
            if (value.some((entry) => typeof entry !== 'string')) return 'Choose from the list.';
            const options = question.options ?? [];
            const unknown = value.find((entry) => !options.includes(entry));
            if (unknown !== undefined) return 'Choose from the list.';
            return checks.skipCombination ? null : combinationMessage(question, value);
        }

        case 'single_select': {
            if (typeof value !== 'string') return 'Choose from the list.';
            // The roster is live, and the journey supplies it at render time,
            // so the bank holds no option list to check against. The caller
            // gives the roster when it has one: the server gives it when the
            // client changes the manager, and the journey gives it before the
            // client moves on. The submission also checks the manager on the
            // application row.
            if (question.optionsFromRoster) {
                return checks.investmentManagerNames
                    ? investmentManagerMessage(value, checks.investmentManagerNames)
                    : null;
            }
            const options = question.options ?? [];
            return options.includes(value) ? null : 'Choose from the list.';
        }

        case 'yes_no':
            return value === 'Yes' || value === 'No' ? null : 'Answer Yes or No.';

        case 'country_select':
            if (typeof value !== 'string') return 'Choose a country from the list.';
            return COUNTRY_CODES.has(value) ? null : 'Choose a country from the list.';

        case 'date': {
            if (typeof value !== 'string' || !isCalendarDate(value)) {
                return 'Enter a date as day, month and year.';
            }
            if (value < CLIENT_APPLICATION_EARLIEST_DATE) return 'Enter a later date.';
            if (value > CLIENT_APPLICATION_LATEST_DATE) return 'Enter an earlier date.';
            return null;
        }

        case 'currency':
            if (typeof value !== 'number') return 'Enter an amount.';
            return numberRangeMessage(value, CLIENT_APPLICATION_AMOUNT_MAX);

        case 'number':
            if (typeof value !== 'number') return 'Enter a number.';
            return numberRangeMessage(value, CLIENT_APPLICATION_NUMBER_MAX);

        case 'long_text':
            if (typeof value !== 'string') return 'Enter your answer.';
            return value.length > CLIENT_APPLICATION_LONG_TEXT_MAX
                ? `Keep your answer to ${CLIENT_APPLICATION_LONG_TEXT_MAX} characters or fewer.`
                : null;

        case 'short_text':
        default:
            if (typeof value !== 'string') return 'Enter your answer.';
            return value.length > CLIENT_APPLICATION_SHORT_TEXT_MAX
                ? `Keep your answer to ${CLIENT_APPLICATION_SHORT_TEXT_MAX} characters or fewer.`
                : null;
    }
}

/**
 * The message for a visible required question that holds nothing. The journey
 * prints this one beside the label and not under the field (SEP-340 item 6).
 */
export const CLIENT_APPLICATION_ANSWER_NEEDED_MESSAGE = 'This answer is needed.';

/** The message for a visible required question that holds nothing. */
export function missingAnswerMessage(question: ClientApplicationQuestion): string {
    return question.kind === 'acknowledgement'
        ? 'Tick the box to accept.'
        : CLIENT_APPLICATION_ANSWER_NEEDED_MESSAGE;
}

// ---------------------------------------------------------------------------
// The whole map
// ---------------------------------------------------------------------------

/** What a caller wants checked. */
export interface ClientApplicationValidationOptions {
    /**
     * Check one section alone. A save checks the section it wrote, and the
     * submission of slice 7 checks the whole bank.
     */
    readonly sectionId?: string;
    /**
     * Also report a visible required question that holds nothing. A SAVE never
     * asks for this, because a half-filled section must still save
     * (SEP-263.18). A step forward and the submission both do.
     */
    readonly requireComplete?: boolean;
    /**
     * The live roster of investment managers, one name for each manager.
     * When the caller gives it, the proposed investment manager must name
     * exactly one manager of it.
     */
    readonly investmentManagerNames?: readonly string[];
    /**
     * The question ids whose mix of choices this call does not check against
     * `combinableOptions`. A save gives the ids of the answers it does not
     * change. The submission gives none, so it refuses every bad mix.
     */
    readonly skipCombinationOf?: readonly string[];
    /**
     * The date of today, `YYYY-MM-DD`, from `clockNow()` of the caller
     * (`clientApplicationCalendarDate`). A rule that compares a date with
     * today reads it. Without it, such a rule gives no error.
     */
    readonly today?: string | null;
    /** The bank to check against. Only a test gives its own bank. */
    readonly bank?: ClientApplicationFormContext['bank'];
    /** The form to check against (SEP-293). Absent means the individual form. */
    readonly form?: ClientApplicationFormDefinition;
}

/**
 * Check a map of answers against the bank.
 *
 * The answers are read as one map, because a `showWhen` reads its neighbour. A
 * caller that checks one section therefore still passes the WHOLE map and names
 * the section, rather than passing the section alone.
 *
 * It checks the current version of the form, because only a draft and a
 * submission are checked. After the check of each question, it runs each form
 * rule of the bank (`client-application-form-rules.ts`). It keeps a rule error
 * only for a visible question of the checked sections that has no error of
 * its own.
 */
export function validateClientApplicationAnswers(
    answers: ClientApplicationAnswers,
    options: ClientApplicationValidationOptions = {},
): readonly ClientApplicationFieldError[] {
    const bank = clientApplicationContextBank({ bank: options.bank, form: options.form });
    const version = clientApplicationContextVersion({ form: options.form });
    const today = options.today ?? null;
    const context: ClientApplicationFormContext = { version, today, bank };
    const allSections = clientApplicationSections(version, bank);
    const sections = options.sectionId
        ? allSections.filter((section) => section.id === options.sectionId)
        : allSections;
    // A map an older bank saved is read in the shape the bank holds now, so an
    // answer that was valid when the client gave it stays valid.
    const current = upgradeClientApplicationAnswers(answers, version, { form: options.form });
    const errors: ClientApplicationFieldError[] = [];
    // The visible questions of the checked sections, with their section id.
    const checked = new Map<string, string>();
    for (const section of sections) {
        for (const question of visibleClientApplicationQuestions(section, current, context)) {
            checked.set(question.id, section.id);
            const value = current[question.id] ?? null;
            const shape = validateClientApplicationAnswer(question, value, {
                investmentManagerNames: options.investmentManagerNames,
                skipCombination: options.skipCombinationOf?.includes(question.id),
            });
            if (shape) {
                errors.push({ sectionId: section.id, questionId: question.id, message: shape });
                continue;
            }
            if (
                options.requireComplete &&
                isClientApplicationQuestionRequired(question, current) &&
                isBlankClientApplicationAnswer(value)
            ) {
                errors.push({
                    sectionId: section.id,
                    questionId: question.id,
                    message: missingAnswerMessage(question),
                });
            }
        }
    }
    const failed = new Set(errors.map((error) => error.questionId));
    const ruleContext = { version, today, requireComplete: options.requireComplete === true };
    for (const rule of bank.formRules) {
        for (const error of rule.check(current, ruleContext)) {
            const sectionId = checked.get(error.questionId);
            if (sectionId === undefined || failed.has(error.questionId)) continue;
            failed.add(error.questionId);
            errors.push({ sectionId, questionId: error.questionId, message: error.message });
        }
    }
    return errors;
}

// ---------------------------------------------------------------------------
// Progress, and where the client stopped
// ---------------------------------------------------------------------------

/** How one section stands, for the progress indication and the stepper. */
export interface ClientApplicationSectionProgress {
    readonly sectionId: string;
    readonly title: string;
    readonly part: number | null;
    /** The short name the stepper prints. It falls back to the title. */
    readonly stepLabel: string;
    /** How many questions the section shows for the answers given so far. */
    readonly visibleCount: number;
    /** How many of those hold an answer. */
    readonly answeredCount: number;
    /** The visible required questions that hold nothing. */
    readonly missingRequired: readonly string[];
    /** True when every visible required question holds an answer. */
    readonly complete: boolean;
}

/**
 * How every section stands, in the order the form prints them. The context
 * gives the date of today to a rule that needs it.
 */
export function clientApplicationProgress(
    answers: ClientApplicationAnswers,
    context: Omit<ClientApplicationFormContext, 'version'> = {},
): readonly ClientApplicationSectionProgress[] {
    const version = clientApplicationContextVersion({ form: context.form });
    const bank = clientApplicationContextBank(context);
    const visibility: ClientApplicationFormContext = { ...context, version, bank };
    const current = upgradeClientApplicationAnswers(answers, version, { form: context.form });
    return clientApplicationSections(version, bank).map((section) => {
        const visible = visibleClientApplicationQuestions(section, current, visibility);
        const missingRequired = visible
            .filter(
                (question) =>
                    isClientApplicationQuestionRequired(question, current) &&
                    isBlankClientApplicationAnswer(current[question.id] ?? null),
            )
            .map((question) => question.id);
        return {
            sectionId: section.id,
            title: section.title,
            part: section.part,
            stepLabel: section.stepLabel ?? section.title,
            visibleCount: visible.length,
            answeredCount: visible.filter(
                (question) => !isBlankClientApplicationAnswer(current[question.id] ?? null),
            ).length,
            missingRequired,
            complete: missingRequired.length === 0,
        };
    });
}

/**
 * The section the journey opens on when a client comes back (SEP-263.8).
 *
 * It is the FIRST section that is not complete. A step forward is gated on the
 * required answers of the section it leaves, so the first incomplete section is
 * exactly the one the client stopped in. With every section complete the client
 * reached the end, so the journey opens on the last section.
 *
 * The resume point is DERIVED, and no column holds it. A stored resume point
 * would be a second truth that a cleared answer could make wrong.
 */
export function clientApplicationResumeSectionId(
    answers: ClientApplicationAnswers,
    context: Omit<ClientApplicationFormContext, 'version'> = {},
): string {
    const progress = clientApplicationProgress(answers, context);
    const stopped = progress.find((section) => !section.complete);
    const last = progress[progress.length - 1];
    return stopped?.sectionId ?? last?.sectionId ?? '';
}

/**
 * The index of one section in the journey, or 0 when the id is unknown. The
 * form is the individual form unless the caller gives one (SEP-293).
 */
export function clientApplicationSectionIndex(
    sectionId: string,
    form?: ClientApplicationFormDefinition,
): number {
    const sections =
        form === undefined
            ? CLIENT_APPLICATION_SECTIONS
            : clientApplicationSections(form.currentVersion, form.bank);
    const found = sections.findIndex((section) => section.id === sectionId);
    return found === -1 ? 0 : found;
}

/** Whether the current version of the form holds this section. */
export function isClientApplicationSectionId(
    sectionId: string,
    form?: ClientApplicationFormDefinition,
): boolean {
    return clientApplicationSection(sectionId, form?.currentVersion, form) !== undefined;
}
